import React, { useState, useEffect, useMemo } from 'react';
import { GlobalButton } from '../GlobalButton';
import { GlobalInput } from '../GlobalInput';
import { GlobalTextarea } from '../GlobalTextarea';
import { GlobalDropdown } from '../GlobalDropdown';
import GlobalTabs from '../GlobalTabs';
import SmallServiceCard from '../SmallServiceCard';
import { URSCard, Requirement } from './data/URSCard';
import styles from './URSTab.module.css';
import { useLocale } from '../../context/DynamicLocaleProvider';
import { useToast } from '../../context/ToastProvider';
import { useTheme } from '../../context/ThemeProvider';
import { FilterBar } from '../FilterBar';
import GlobalEmptyState from '../GlobalEmptyState';
import GlobalSidebar from '../GlobalSidebar';
import { FSAutoComplete } from '../FSAutoComplete';
import { TestAutoComplete } from '../TestAutoComplete';
import { RiskAutoComplete } from '../RiskAutoComplete';
import { SOPAutoComplete } from '../SOPAutoComplete';
import DoubleConfirmationModal from '../DoubleConfirmationModal';
import { UserRequirement } from '../../types';
import { createRequirement } from '../../services/domainEntities';
import { collection, getDocs, doc, deleteDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useUser } from '../../context/UserContext';
import EntityGenerationSidebar from '../EntityGenerationSidebar';
import TransformEntitiesSidebar from '../TransformEntitiesSidebar';
import GenerateEntitySidebar from '../GenerateEntitySidebar';
import AddDocumentSidebar from '../AddDocumentSidebar';
import { exportToCSV } from '../../utils/exportToCSV';

interface URSTabProps {
  projectId: string;
  onExport?: () => void;
  onOpenAiSidebar?: () => void;
  onNavigateToEntity?: (entityId: string, entityType: 'URS' | 'FS' | 'TEST' | 'RISK' | 'SOP') => void;
}

export const URSTab: React.FC<URSTabProps> = ({ projectId, onExport, onOpenAiSidebar, onNavigateToEntity }) => {
  const { t } = useLocale();
  const showToast = useToast();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { user } = useUser();

  const [requirements, setRequirements] = useState<UserRequirement[]>([]);
  const [selectedRequirement, setSelectedRequirement] = useState<UserRequirement | null>(null);
  const [showRequirementEditor, setShowRequirementEditor] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [requirementToDelete, setRequirementToDelete] = useState<UserRequirement | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showAddDocumentSidebar, setShowAddDocumentSidebar] = useState(false);
  const [showEntityGenerationSidebar, setShowEntityGenerationSidebar] = useState(false);
  const [selectedEntitiesForGeneration, setSelectedEntitiesForGeneration] = useState<Array<{
    id: string;
    type: 'URS' | 'FS' | 'TEST' | 'RISK' | 'SOP';
    title: string;
  }>>([]);
  const [showTransformEntitiesSidebar, setShowTransformEntitiesSidebar] = useState(false);
  const [showGenerateEntitySidebar, setShowGenerateEntitySidebar] = useState(false);

  // Load requirements from Firestore on mount
  useEffect(() => {
    const loadRequirements = async () => {
      if (!projectId) {
        return;
      }
      
      try {
        const col = collection(db, 'projects', projectId, 'urs');
        const snapshot = await getDocs(col);
        const loaded: UserRequirement[] = snapshot.docs.map(doc => {
          const data = doc.data();
          // Use custom ID from data if it exists, otherwise generate one from Firestore ID
          return {
            ...data,
            id: data.id || `URS-${doc.id.substring(0, 8)}`, // Use custom ID or generate from Firestore ID
            _firestoreId: doc.id // Keep Firestore ID for operations
          } as UserRequirement & { _firestoreId: string };
        });
        setRequirements(loaded);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error loading requirements:', err);
        }
        showToast(t('project_tab_requirement_load_failed') || 'Failed to load requirements', 'error');
      }
    };

    loadRequirements();
  }, [projectId, showToast, t]);

  const handleAddRequirement = () => {
    // Generate next custom ID based on existing requirements
    const nextNumber = requirements.length + 1;
    const customId = `URS-${String(nextNumber).padStart(3, '0')}`;
    
    const newReq: UserRequirement = {
      id: customId, // Custom ID like URS-001, URS-002, etc.
      type: 'URS',
      title: '',
      description: '',
      rationale: '',
      category: 'Security',
      gxp_relevance: 'High',
      business_priority: 'Must',
      status: 'Draft',
      links: {
        fs_ids: [],
        test_ids: [],
        risk_ids: [],
        sop_ids: []
      },
      meta: {
        owner: '',
        tags: []
      },
      createdAt: new Date(),
      updatedAt: new Date()
    };
    setSelectedRequirement(newReq);
    setShowRequirementEditor(true);
  };

  const handleSaveRequirement = async (req: UserRequirement) => {
    try {

      // Check if this is an update (has _firestoreId) or new requirement
      const firestoreId = (req as any)?._firestoreId;
      
      if (firestoreId) {
        // Update existing requirement
        const docRef = doc(db, 'projects', projectId, 'urs', firestoreId);
        const { _firestoreId, ...dataToSave } = req as any;
        await updateDoc(docRef, {
          ...dataToSave,
          updatedAt: serverTimestamp()
        } as any);

      } else {
        // Create new requirement
        await createRequirement(projectId, req);

      }
      
      // Reload requirements from Firestore to ensure we have the latest data
      const col = collection(db, 'projects', projectId, 'urs');
      const snapshot = await getDocs(col);
      const loaded: UserRequirement[] = snapshot.docs.map(doc => {
        const data = doc.data();
        // Use custom ID from data if it exists, otherwise generate from Firestore ID
        return {
          ...data,
          id: data.id || `URS-${doc.id.substring(0, 8)}`, // Use custom ID or generate from Firestore ID
          _firestoreId: doc.id // Keep Firestore ID for operations
        } as UserRequirement & { _firestoreId: string };
      });

      setRequirements(loaded);
      
      setShowRequirementEditor(false);
      setSelectedRequirement(null);
      showToast(t('project_tab_requirement_saved') || 'Requirement saved', 'success');
    } catch (err) {
      console.error('❌ Error saving requirement:', err);
      showToast(t('project_tab_requirement_save_failed') || 'Failed to save requirement', 'error');
    }
  };

  const handleDeleteRequirement = (reqId: string) => {
    // Find the requirement and show confirmation modal
    const req = requirements.find(r => r.id === reqId);
    if (req) {
      setRequirementToDelete(req);
      setShowDeleteModal(true);
    }
  };

  const confirmDeleteRequirement = async () => {
    if (!requirementToDelete) return;

    try {
      setIsDeleting(true);
      const firestoreId = (requirementToDelete as any)?._firestoreId || requirementToDelete.id;
      
      // Delete from Firestore using Firestore document ID
      const docRef = doc(db, 'projects', projectId, 'urs', firestoreId);
      await deleteDoc(docRef);
      
      // Update local state using custom ID
      setRequirements(requirements.filter(r => r.id !== requirementToDelete.id));
      if (selectedRequirement?.id === requirementToDelete.id) {
        setSelectedRequirement(null);
        setShowRequirementEditor(false);
      }
      showToast(t('project_tab_requirement_deleted') || 'Requirement deleted', 'success');
      setShowDeleteModal(false);
      setRequirementToDelete(null);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Error deleting requirement:', err);
      }
      showToast(t('project_tab_requirement_delete_failed') || 'Failed to delete requirement', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAiGenerate = async () => {
    // Open the Generate Entity sidebar
    setShowGenerateEntitySidebar(true);
  };

  const handleComplete = (req: UserRequirement) => {
    // Open Entity Generation Sidebar with this specific URS selected
    setSelectedEntitiesForGeneration([{
      id: req.id,
      type: 'URS',
      title: req.title
    }]);
    setShowEntityGenerationSidebar(true);
  };

  const handleGenerateFromAllURS = () => {
    // Open Entity Generation Sidebar with ALL URS selected
    if (requirements.length === 0) {
      showToast(t('project_tab_no_urs_to_generate') || 'No URS available. Please create a URS first.', 'error');
      return;
    }
    const allURS = requirements.map(req => ({
      id: req.id,
      type: 'URS' as const,
      title: req.title
    }));
    setSelectedEntitiesForGeneration(allURS);
    setShowEntityGenerationSidebar(true);
  };

  const handleTransformEntities = () => {
    setShowTransformEntitiesSidebar(true);
  };

  const handleAiEnhance = async (req: Requirement) => {
    // Simulate AI enhancement
    setTimeout(() => {
      const enhanced = {
        ...req,
        description: `${req.description}\n\n[AI Enhanced: Additional context and details added]`,
        updatedAt: new Date()
      };
      setRequirements(requirements.map(r => r.id === req.id ? enhanced : r));
      showToast(t('project_tab_ai_enhanced') || 'Requirement enhanced', 'success');
    }, 1500);
  };

  const handleUploadURSClick = () => {
    setShowAddDocumentSidebar(true);
  };

  // Helper function to extract numeric part from URS ID (e.g., "URS-001" -> 1)
  const extractURSNumber = (id: string): number => {
    const match = id.match(/(\d+)$/); // Extract trailing numbers
    return match ? parseInt(match[1], 10) : 0;
  };

  // Filter and sort requirements based on search and status
  const filteredRequirements = useMemo(() => {
    const filtered = requirements.filter(req => {
      const matchesSearch = !searchValue ||
        req.id.toLowerCase().includes(searchValue.toLowerCase()) ||
        req.title.toLowerCase().includes(searchValue.toLowerCase()) ||
        req.description.toLowerCase().includes(searchValue.toLowerCase());
      const matchesStatus = !statusFilter || req.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
    
    // Sort by numeric part of ID (URS-001, URS-002, etc.)
    return filtered.sort((a, b) => {
      const numA = extractURSNumber(a.id);
      const numB = extractURSNumber(b.id);
      return numA - numB;
    });
  }, [requirements, searchValue, statusFilter]);

  const tabs = [
    {
      label: t('project_tab_requirements') || 'Requirements',
      queryValue: 'requirements',
      content: (
        <div className={styles.requirementsSection}>
          {/* FilterBar */}
          <FilterBar
            filters={[
              {
                id: 'status',
                label: t('project_tab_status') || 'Status',
                options: [
                  { value: '', label: t('project_tab_all') || 'All' },
                  { value: 'Draft', label: t('project_tab_status_draft') || 'Draft' },
                  { value: 'In Review', label: t('project_tab_status_in_review') || 'In Review' },
                  { value: 'Approved', label: t('project_tab_status_approved') || 'Approved' },
                  { value: 'Retired', label: t('project_tab_status_rejected') || 'Retired' }
                ],
                value: statusFilter,
                onChange: (value) => setStatusFilter(value)
              }
            ]}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchPlaceholder={t('project_tab_search_requirements') || 'Search requirements...'}
            showInviteButton={true}
            onAddUser={handleAddRequirement}
            addButtonText={t('project_tab_add_requirement') || 'Add Requirement'}
          />

          {/* Requirements List */}
          {filteredRequirements.length === 0 ? (
            <GlobalEmptyState
              buttonText={t('project_tab_add_requirement') || 'Add Requirement'}
              buttonIcon={isDarkMode ? "/assets/icons/plus.circle.svg" : "/assets/icons/plus.circle.black.svg"}
              onButtonClick={handleAddRequirement}
            />
          ) : (
            <div className={styles.requirementsList}>
              {filteredRequirements.map((req) => {
                // Use Firestore document ID as key to avoid duplicate key warnings
                const firestoreId = (req as any)?._firestoreId || req.id;
                return (
                  <div key={firestoreId} className={styles.requirementItem}>
                    <URSCard
                      requirement={req}
                      projectId={projectId}
                      onEdit={(req) => {
                        setSelectedRequirement(req);
                        setShowRequirementEditor(true);
                      }}
                      onDelete={handleDeleteRequirement}
                      onAiEnhance={handleAiEnhance}
                      onViewTraceability={(req) => {
                        showToast(t('project_tab_viewing_traceability') || 'Viewing traceability', 'info');
                      }}
                      onComplete={handleComplete}
                      onEntityClick={(entityId, entityType) => {
                        onNavigateToEntity?.(entityId, entityType);
                      }}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )
    },
    {
      label: t('project_tab_options') || 'Options',
      queryValue: 'options',
      content: (
        <div className={styles.optionsGrid}>

          {/* Add requirement */}
          <SmallServiceCard
            id="add-requirement"
            title={t('project_tab_add_requirement') || 'Add Requirement'}
            description={t('project_tab_add_requirement_desc') || 'Create a new user requirement specification'}
            fileTypeIcon=""
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/plus.circle.svg" : "/assets/icons/plus.circle.black.svg"}
            buttonText={t('project_tab_add') || 'Add'}
            buttonOnClick={handleAddRequirement}
          />

          {/* Import file with AI */}
          <SmallServiceCard
            id="import-file-with-ai-urs"
            title={t('project_tab_read_file_with_ai') || 'Datei mit KI einlesen'}
            description={t('project_tab_read_file_with_ai_urs_desc') || 'Importieren Sie Dateien um Anforderungen automatisch auslesen und erstellen zu lassen.'}
            fileTypeIcon={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/icloud.and.arrow.up.svg" : "/assets/icons/icloud.and.arrow.up.black.svg"}
            buttonText={t('project_tab_import') || 'Importieren'}
            buttonOnClick={handleUploadURSClick}
            disabled={!user}
          />

          {/* Generate with AI */}
          <SmallServiceCard
            id="generate-ai-urs"
            title={t('project_tab_generate_with_ai') || 'Generate with AI'}
            description={t('project_tab_generate_with_ai_desc') || 'Generate requirements using AI based on your prompt'}
            fileTypeIcon={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.right.circle.svg" :"/assets/icons/arrow.right.circle.black.svg"}
            buttonText={t('project_tab_generate') || 'Generate'}
            buttonOnClick={handleAiGenerate}
          />

          {/* Generate from ALL URS */}
          <SmallServiceCard
            id="generate-from-all-urs"
            title={t('project_tab_ai_generate_from_all_urs') || 'AI Generate from ALL URS'}
            description={t('project_tab_ai_generate_from_all_urs_desc') || 'Generate entities from all URS using AI'}
            fileTypeIcon={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.right.circle.svg" :"/assets/icons/arrow.right.circle.black.svg"}
            buttonText={t('project_tab_generate') || 'Generate'}
            buttonOnClick={handleGenerateFromAllURS}
          />

          {/* Transform Entities */}
          <SmallServiceCard
            id="transform-entities"
            title={t('project_tab_transform_urs') || 'Transform Requirement'}
            description={t('project_tab_transform_urs_desc') || 'Transform requirements to replace content, translate, or rephrase.'}
            fileTypeIcon={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.right.circle.svg" :"/assets/icons/arrow.right.circle.black.svg"}
            buttonText={t('project_tab_transform') || 'Transform'}
            buttonOnClick={handleTransformEntities}
          />

          {/* Export PDF */}
          <SmallServiceCard
            id="export-pdf-urs"
            title={t('project_tab_urs_export_pdf') || 'Export as PDF'}
            description={t('project_tab_urs_export_pdf_desc') || 'Export all requirements together as a PDF document'}
            fileTypeIcon="/assets/graphics/pdf-icon.svg"
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.down.circle.white.svg" : "/assets/icons/arrow.down.circle.black.svg"}
            buttonText={t('project_tab_export') || 'Export'}
            buttonOnClick={onExport || (() => showToast(t('project_tab_exporting') || 'Exporting...', 'info'))}
            disabled
          />

          {/* Export CSV */}
          <SmallServiceCard
            id="export-csv-urs"
            title={t('project_tab_urs_export_csv') || 'Export as CSV'}
            description={t('project_tab_urs_export_csv_desc') || 'Export all requirements together as a CSV document'}
            fileTypeIcon={"/assets/graphics/csv_icon.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.down.circle.white.svg" : "/assets/icons/arrow.down.circle.black.svg"}
            buttonText={t('project_tab_export') || 'Export'}
            buttonOnClick={() => {
              try {
                if (requirements.length === 0) {
                  showToast(t('project_tab_no_urs_to_export') || 'No requirements to export', 'warning');
                  return;
                }
                exportToCSV(requirements, `URS_Export_${projectId}_${new Date().toISOString().split('T')[0]}`);
                showToast(t('project_tab_export_success') || 'Export successful', 'success');
              } catch (error) {
                console.error('Error exporting CSV:', error);
                showToast(t('project_tab_export_failed') || 'Export failed', 'error');
              }
            }}
          />
        </div>
      )
    }
  ];

  return (
    <div className={styles.ursTab}>
      {/* Header */}
      <div className={styles.tabHeader}>
        <h2 className={styles.tabTitle}>
          {t('project_page_tab_urs') || 'URS (User Requirement Specifications)'}
        </h2>
      </div>

      <GlobalTabs tabs={tabs} queryParam="tab" defaultTabValue="requirements" />

      {/* Requirement Editor Modal */}
      {selectedRequirement && (
        <RequirementEditor
          projectId={projectId}
          requirement={selectedRequirement}
          onSave={async (req) => {
            await handleSaveRequirement(req);
          }}
          onCancel={() => {
            setShowRequirementEditor(false);
            setSelectedRequirement(null);
          }}
          isOpen={showRequirementEditor}
          isNew={!(selectedRequirement as any)?._firestoreId}
        />
      )}

      {/* Entity Generation Sidebar */}
      <EntityGenerationSidebar
        isOpen={showEntityGenerationSidebar}
        onClose={() => {
          setShowEntityGenerationSidebar(false);
          setSelectedEntitiesForGeneration([]);
        }}
        projectId={projectId}
        selectedEntities={selectedEntitiesForGeneration}
        onSuccess={() => {
          // Reload requirements to show any new entities
          const loadRequirements = async () => {
            try {
              const col = collection(db, 'projects', projectId, 'urs');
              const snapshot = await getDocs(col);
              const loaded: UserRequirement[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  ...data,
                  id: data.id || `URS-${doc.id.substring(0, 8)}`,
                  _firestoreId: doc.id
                } as UserRequirement & { _firestoreId: string };
              });
              setRequirements(loaded);
            } catch (err) {
              if (process.env.NODE_ENV !== 'production') {
                console.error('Error reloading requirements:', err);
              }
            }
          };
          loadRequirements();
        }}
      />

      {/* Transform Entities Sidebar */}
      <TransformEntitiesSidebar
        isOpen={showTransformEntitiesSidebar}
        onClose={() => {
          setShowTransformEntitiesSidebar(false);
        }}
        projectId={projectId}
        onSuccess={() => {
          // Reload requirements to show transformed entities
          const loadRequirements = async () => {
            try {
              const col = collection(db, 'projects', projectId, 'urs');
              const snapshot = await getDocs(col);
              const loaded: UserRequirement[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  ...data,
                  id: data.id || `URS-${doc.id.substring(0, 8)}`,
                  _firestoreId: doc.id
                } as UserRequirement & { _firestoreId: string };
              });
              setRequirements(loaded);
            } catch (err) {
              if (process.env.NODE_ENV !== 'production') {
                console.error('Error reloading requirements:', err);
              }
            }
          };
          loadRequirements();
        }}
      />

      {/* Generate Entity Sidebar */}
      <GenerateEntitySidebar
        isOpen={showGenerateEntitySidebar}
        onClose={() => {
          setShowGenerateEntitySidebar(false);
        }}
        projectId={projectId}
        entityType="URS"
        onSuccess={() => {
          // Reload requirements to show generated entity
          const loadRequirements = async () => {
            try {
              const col = collection(db, 'projects', projectId, 'urs');
              const snapshot = await getDocs(col);
              const loaded: UserRequirement[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  ...data,
                  id: data.id || `URS-${doc.id.substring(0, 8)}`,
                  _firestoreId: doc.id
                } as UserRequirement & { _firestoreId: string };
              });
              setRequirements(loaded);
            } catch (err) {
              if (process.env.NODE_ENV !== 'production') {
                console.error('Error reloading requirements:', err);
              }
            }
          };
          loadRequirements();
        }}
      />

      {/* Add Document Sidebar */}
      <AddDocumentSidebar
        isOpen={showAddDocumentSidebar}
        onClose={() => setShowAddDocumentSidebar(false)}
        projectId={projectId}
        defaultEntityTypes={['URS']}
        onSuccess={() => {
          // Reload requirements after document upload
          const loadRequirements = async () => {
            try {
              const col = collection(db, 'projects', projectId, 'urs');
              const snapshot = await getDocs(col);
              const loaded: UserRequirement[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  ...data,
                  id: data.id || `URS-${doc.id.substring(0, 8)}`,
                  _firestoreId: doc.id
                } as UserRequirement & { _firestoreId: string };
              });
              setRequirements(loaded);
            } catch (err) {
              if (process.env.NODE_ENV !== 'production') {
                console.error('Error reloading requirements:', err);
              }
            }
          };
          loadRequirements();
        }}
      />

      {/* Delete confirmation modal */}
      <DoubleConfirmationModal
        isOpen={showDeleteModal}
        title={`${t('project_tab_delete_requirement') || 'Delete Requirement'} "${requirementToDelete?.id || ''}"?`}
        paragraph={t('project_tab_delete_requirement_confirmation') || 'Are you sure you want to delete this requirement? This action cannot be undone.'}
        primaryLabel={t('global_delete') || 'Delete'}
        primaryIcon="/assets/icons/trash.red.svg"
        secondaryLabel={t('document_page_cancel') || 'Cancel'}
        secondaryIcon="/assets/icons/xmark.svg"
        onPrimary={confirmDeleteRequirement}
        onSecondary={() => {
          setShowDeleteModal(false);
          setRequirementToDelete(null);
        }}
        primaryDestructive
        loading={isDeleting}
      />
    </div>
  );
};

// Requirement Editor Component
interface RequirementEditorProps {
  projectId: string;
  requirement: Requirement;
  onSave: (req: Requirement) => Promise<void>;
  onCancel: () => void;
  isOpen: boolean;
  isNew: boolean;
}

const RequirementEditor: React.FC<RequirementEditorProps> = ({
  projectId,
  requirement,
  onSave,
  onCancel,
  isOpen,
  isNew
}) => {
  const { t } = useLocale();
  const [editedReq, setEditedReq] = useState<Requirement>(requirement);

  if (!isOpen) return null;

  return (
    <GlobalSidebar isOpen={isOpen} onClose={onCancel}>
      <div>
        <div className={styles.header}>
          <h2>
            {isNew
              ? (t('project_tab_urs_add') || 'Add URS')
              : (t('project_tab_urs_edit') || 'Edit URS')}
            <span className={styles.ursId}> {editedReq.id}</span>
          </h2>
        </div>

        <form className={styles.form} onSubmit={async (e) => { e.preventDefault(); await onSave(editedReq); }}>
          <div className={styles.modalTabs}>
            <GlobalTabs
              tabs={[
                {
                  label: t('project_tab_req_details') || 'Details',
                  content: (
                    <div className={styles.modalTabSection}>
                      <GlobalInput
                        label={t('project_tab_title') || 'Title'}
                        value={editedReq.title}
                        onChange={(e) => setEditedReq({ ...editedReq, title: e.target.value })}
                      />
                      <GlobalTextarea
                        label={t('project_tab_description') || 'Description'}
                        value={editedReq.description}
                        onChange={(e) => setEditedReq({ ...editedReq, description: e.target.value })}
                        rows={3}
                      />
                      <GlobalTextarea
                        label={t('project_tab_rationale') || 'Rationale'}
                        value={editedReq.rationale}
                        onChange={(e) => setEditedReq({ ...editedReq, rationale: e.target.value })}
                        rows={2}
                      />
                    </div>
                  )
                },
                {
                  label: t('project_tab_req_categorization') || 'Categorization',
                  content: (
                    <div className={styles.modalTabSection}>
                      <GlobalDropdown
                        label={t('project_tab_category') || 'Category'}
                        value={editedReq.category}
                        onChange={(value) => setEditedReq({ ...editedReq, category: value as any })}
                        options={[
                          { value: 'Security', label: t('project_tab_category_security') || 'Security' },
                          { value: 'Data Integrity', label: t('project_tab_category_data_integrity') || 'Data Integrity' },
                          { value: 'Performance', label: t('project_tab_category_performance') || 'Performance' },
                          { value: 'Usability', label: t('project_tab_category_usability') || 'Usability' },
                          { value: 'Regulatory', label: t('project_tab_category_regulatory') || 'Regulatory' }
                        ]}
                      />
                      <GlobalDropdown
                        label={t('project_tab_gxp_relevance') || 'GxP Relevance'}
                        value={editedReq.gxp_relevance}
                        onChange={(value) => setEditedReq({ ...editedReq, gxp_relevance: value as any })}
                        options={[
                          { value: 'None', label: t('project_tab_gxp_none') || 'None' },
                          { value: 'Low', label: t('project_tab_gxp_low') || 'Low' },
                          { value: 'Medium', label: t('project_tab_gxp_medium') || 'Medium' },
                          { value: 'High', label: t('project_tab_gxp_high') || 'High' }
                        ]}
                      />
                      <GlobalDropdown
                        label={t('project_tab_business_priority') || 'Business Priority'}
                        value={editedReq.business_priority}
                        onChange={(value) => setEditedReq({ ...editedReq, business_priority: value as any })}
                        options={[
                          { value: 'Must', label: t('project_tab_priority_must') || 'Must' },
                          { value: 'Should', label: t('project_tab_priority_should') || 'Should' },
                          { value: 'Could', label: t('project_tab_priority_could') || 'Could' }
                        ]}
                      />
                      <GlobalDropdown
                        label={t('project_tab_status') || 'Status'}
                        value={editedReq.status}
                        onChange={(value) => setEditedReq({ ...editedReq, status: value as any })}
                        options={[
                          { value: 'Draft', label: t('project_tab_status_draft') || 'Draft' },
                          { value: 'In Review', label: t('project_tab_status_in_review') || 'In Review' },
                          { value: 'Approved', label: t('project_tab_status_approved') || 'Approved' },
                          { value: 'Retired', label: t('project_tab_status_rejected') || 'Retired' }
                        ]}
                      />
                    </div>
                  )
                },
                {
                  label: t('project_tab_req_links') || 'Connections',
                  content: (
                    <div className={styles.modalTabSection}>
                      <FSAutoComplete
                        projectId={projectId}
                        label={t('project_tab_linked_fs') || 'Linked FS'}
                        placeholder={t('project_tab_linked_fs_placeholder') || 'Search Functional Specs...'}
                        selectedIds={editedReq.links.fs_ids}
                        currentEntityId={editedReq.id}
                        currentEntityType="URS"
                        onChange={(ids) =>
                          setEditedReq({
                            ...editedReq,
                            links: { ...editedReq.links, fs_ids: ids }
                          })
                        }
                      />
                      <TestAutoComplete
                        projectId={projectId}
                        label={t('project_tab_linked_tests') || 'Linked TEST'}
                        placeholder={t('project_tab_linked_tests_placeholder') || 'Search Test Cases...'}
                        selectedIds={editedReq.links.test_ids}
                        currentEntityId={editedReq.id}
                        currentEntityType="URS"
                        onChange={(ids) =>
                          setEditedReq({
                            ...editedReq,
                            links: { ...editedReq.links, test_ids: ids }
                          })
                        }
                      />
                      <RiskAutoComplete
                        projectId={projectId}
                        label={t('project_tab_linked_risks') || 'Linked RISK'}
                        placeholder={t('project_tab_linked_risks_placeholder') || 'Search Risks...'}
                        selectedIds={editedReq.links.risk_ids}
                        currentEntityId={editedReq.id}
                        currentEntityType="URS"
                        onChange={(ids) =>
                          setEditedReq({
                            ...editedReq,
                            links: { ...editedReq.links, risk_ids: ids }
                          })
                        }
                      />
                      <SOPAutoComplete
                        projectId={projectId}
                        label={t('project_tab_linked_sops') || 'Linked SOP'}
                        placeholder={t('project_tab_linked_sops_placeholder') || 'Search SOPs...'}
                        selectedIds={editedReq.links.sop_ids}
                        currentEntityId={editedReq.id}
                        currentEntityType="URS"
                        onChange={(ids) =>
                          setEditedReq({
                            ...editedReq,
                            links: { ...editedReq.links, sop_ids: ids }
                          })
                        }
                      />
                    </div>
                  )
                }
              ]}
            />
          </div>

          {/* Actions */}
          <div className={styles.actions}>
            <GlobalButton
              type="button"
              onClick={onCancel}
              variant="secondary"
            >
              {t('project_tab_cancel') || 'Cancel'}
            </GlobalButton>
            <GlobalButton
              type="submit"
            >
              {t('project_tab_save') || 'Save'}
            </GlobalButton>
          </div>
        </form>
      </div>
    </GlobalSidebar>
  );
};

